import { format, parse } from 'date-fns';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { IMaskInput } from 'react-imask';
import { DATE_STRING_FORMAT } from '../../Constants/stringFormats';
import getClassNames from '../../Utils/getClassNames';
import './Input.css';
import { convertDateIfApplicable } from '../../Utils/stringUtils';
import InformationPopover from './InformationPopover';

const Input = forwardRef(({ warningPattern, warningMessage, ...props }, ref) => {
    const handleOnChange = (e) => {
        if (props.onChange) {
            props.onChange(e.target.value);
            // Update validation on every change when there is a warning so the user knows immediately
            if (isWarning) {
                validate(e.target.value);
            }
        }
    };

    const handleOnBlur = (e) => {
        validate(e.target.value);
    }

    const validate = (value) => {
        if (warningPattern && !new RegExp(warningPattern).test(value)) {
            setIsWarning(true);
        } else {
            setIsWarning(false);
        }
    };

    const handleOnDateChange = (value) => {
        if (props.onChange) {
            props.onChange(value);
        }
    };

    const handleOnKeyDown = (e) => {
        if (props.onKeyDown) {
            props.onKeyDown(e);
        }
    }

    // Handle clearning warnings if something else is changing the prop value other than normal input on Change
    useEffect(() => {
        if (isWarning) {
            validate(props.value);
        }
    }, [props.value])

    useEffect(() => {
        if (props.type === 'number') {
            const disableScroll = (e) => {
                if (e.target === document.activeElement) {
                    e.preventDefault();
                }
            };

            const inputReference = document.getElementById(props.id);

            inputReference.addEventListener('wheel', disableScroll);

            return () => {
                inputReference.removeEventListener('wheel', disableScroll);
            }
        }
    }, [props.id, props.type]);

    const convertedDate = useMemo(() => {
        if (props.type === 'date') {
            return convertDateIfApplicable(props.value);
        }

        return undefined;
    }, [props.value, props.type]);

    const additonalAttributes = props.additonalAttributes || {};

    const placeholder = props.placeholder || (props.type === 'date' && DATE_STRING_FORMAT.toUpperCase()) || undefined;

    const [isWarning, setIsWarning] = useState(false);
    return (
        <div className="row align-items-center">
            <ColDivOrPassThrough labelLeft={props.labelLeft}>
                {props.label && (<div className={props.labelLeft ? "col" : null}>
                    <label className={props.labelClassName} htmlFor={props.id}>{props.label}</label>
                    {(props.value && placeholder && props.preservePlaceholder && <label className="ms-2">({placeholder})</label>) || null}

                </div>)}
                {props.superscript && (<div className={props.labelLeft ? "col" : null}>
                    <sup> {props.superscript}</sup>
                </div>)}
                {props.note && (<div className={props.labelLeft ? "col" : null}>
                    <br />
                    <label className={props.noteClassName} htmlFor={props.id}>{props.note}</label>
                </div>)}
                {props.type === 'date' && <div className={props.labelLeft ? (props.inputWidthClass ?? "col") : null}>
                    <IMaskInput mask={Date}
                        value={convertedDate}
                        pattern="m/`d/`Y"
                        onAccept={(value, _mask) => handleOnDateChange(value)}
                        placeholder={placeholder}
                        id={props.id}
                        lazy={false}
                        disabled={props.disabled}
                        onKeyDown={handleOnKeyDown}
                        {...additonalAttributes}
                        blocks={{
                            m: {
                                mask: IMask.MaskedRange,
                                from: 1,
                                to: 31,
                                maxLength: 2,
                            },
                            d: {
                                mask: IMask.MaskedRange,
                                from: 1,
                                to: 31,
                                maxLength: 2,
                            },
                            Y: {
                                mask: IMask.MaskedRange,
                                from: 1900,
                                to: 9999,
                                maxLength: 4
                            }
                        }}
                        format={(date) => {
                            return format(date, 'MM/dd/yyyy');
                        }}
                        parse={(str) => {
                            return parse(str, 'MM/dd/yyyy', new Date());
                        }}
                        className={getClassNames('form-control', props.className)} />
                </div>
                }
                {props.type !== 'date' && <div className={props.labelLeft ? (props.inputWidthClass ?? "col") : null}>
                    <input type={props.type || "text"}
                        ref={ref}
                        autoComplete="off"
                        spellCheck="false"
                        value={props.value}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        disabled={props.disabled}
                        id={props.id}
                        onKeyDown={handleOnKeyDown}
                        name={props.name || props.id}
                        className={getClassNames('form-control', `${props.className} ${isWarning ? 'warning' : ''}`)}
                        placeholder={placeholder}
                        maxLength={props.maxLength}
                        {...additonalAttributes} />
                    {(isWarning && warningMessage) && (
                        <span
                            className='text-warning'
                        >
                            {warningMessage}
                        </span>
                    )}
                </div>
                }
            </ColDivOrPassThrough>
            {props.information && (<div className={getClassNames('col-auto', props.label && 'mt-4')}>
                <InformationPopover content={props.information} />
            </div>)}
        </div>
    )
});

const ColDivOrPassThrough = ({ labelLeft, children }) =>
    labelLeft ? <> {children} </> : <div className="col"> {children} </div>;

export default Input;
